/**
 * KAN-835: уведомления "Требуют внимания" на это устройство (Web Push).
 *
 * Статус и ключ VAPID: GET /managers/me/push/ (только вход с записью
 * менеджера). Разрешение браузера спрашивается только в enable(), то есть по
 * нажатию пользователя (иначе iPhone и Chrome его не покажут). Если
 * устройство подписано, а backend подписку уже забыл (ушла по 410), подписка
 * отправляется заново.
 *
 * KAN-868: состояние устройства (подписка и разрешение) общее для всех мест,
 * где хук смонтирован (плашка настройки, профиль, экран "Начало работы"):
 * оно лежит в кеше react-query под PUSH_DEVICE_KEY, enable/disable пишут
 * туда же. Повторная отправка забытой подписки тоже общий запрос (один POST
 * на подписку, сколько бы экземпляров хука ни было). При возврате в
 * приложение (visibilitychange) состояние перечитывается: менеджер мог
 * разрешить или запретить уведомления в настройках телефона.
 *
 * view:
 *   loading      - ещё не знаем;
 *   unsupported  - браузер не умеет Web Push;
 *   ios-install  - iPhone без установки на экран Домой;
 *   denied       - уведомления запрещены в настройках;
 *   on / off     - это устройство подписано / нет.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { pushSubscribeErrorText } from '@/lib/managerNotificationsTexts';
import {
  defaultPushEnv,
  deviceSubscription,
  notificationPermission,
  pushSupport,
  requestNotificationPermission,
  subscribeDevice,
  subscriptionBody,
  type PushEnv,
} from '@/lib/webPush';
import { managerNotificationsApi, type ManagerNotificationsStatus } from '@/services/api/managerNotifications';
import { MANAGERS_QUERY_KEY } from '@/services/api/queryKeys';

export const MY_NOTIFICATIONS_KEY = [...MANAGERS_QUERY_KEY, 'me', 'notifications'] as const;
/** KAN-868: подписка этого устройства и разрешение браузера, общие для всех экземпляров хука. */
export const PUSH_DEVICE_KEY = [...MANAGERS_QUERY_KEY, 'me', 'push-device'] as const;
const PUSH_RESYNC_KEY = [...PUSH_DEVICE_KEY, 'resync'] as const;

export type PushView = 'loading' | 'unsupported' | 'ios-install' | 'denied' | 'on' | 'off';

export interface PushDeviceState {
  permission: NotificationPermission;
  subscription: PushSubscription | null;
}

export function useMyNotificationsStatus() {
  const { data: user } = useCurrentUser();
  const hasManager = !!user?.manager_id;
  const query = useQuery({
    queryKey: MY_NOTIFICATIONS_KEY,
    queryFn: managerNotificationsApi.getStatus,
    enabled: hasManager,
    staleTime: 60_000,
  });
  return { ...query, hasManager };
}

async function readDeviceState(env: PushEnv): Promise<PushDeviceState> {
  let subscription: PushSubscription | null = null;
  try {
    subscription = await deviceSubscription(env);
  } catch {
    subscription = null;
  }
  return { permission: notificationPermission(env), subscription };
}

/** Общее состояние устройства; перечитывается при возврате в приложение. */
function usePushDeviceState(env: PushEnv, supported: boolean) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: PUSH_DEVICE_KEY,
    queryFn: () => readDeviceState(env),
    enabled: supported,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    structuralSharing: false,
  });

  useEffect(() => {
    if (!supported || typeof document === 'undefined') return undefined;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      // cancelRefetch: false - несколько экземпляров хука делят одно чтение.
      void client.invalidateQueries({ queryKey: PUSH_DEVICE_KEY, exact: true }, { cancelRefetch: false });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [client, supported]);

  return query;
}

/**
 * Устройство подписано, а backend подписку забыл: отправить её заново.
 * Общий запрос по endpoint: react-query делит его между экземплярами хука,
 * поэтому POST один. Упавший POST не повторяется при каждом монтировании
 * (retryOnMount: false). Если backend забудет ту же подписку второй раз за
 * сессию, она не отправится заново до перезагрузки приложения (или disable).
 */
function usePushResync(subscription: PushSubscription | null | undefined, needed: boolean): void {
  const client = useQueryClient();
  useQuery({
    queryKey: [...PUSH_RESYNC_KEY, subscription?.endpoint ?? ''],
    queryFn: async (): Promise<ManagerNotificationsStatus> => {
      if (!subscription) throw new Error('no device subscription');
      const data = await managerNotificationsApi.subscribe(subscriptionBody(subscription));
      client.setQueryData(MY_NOTIFICATIONS_KEY, data);
      return data;
    },
    enabled: needed && !!subscription,
    staleTime: Infinity,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/** env: окружение браузера (в тестах подменяется; передавать стабильный объект). */
export function usePushSubscription(envArg?: PushEnv) {
  const env = useMemo(() => envArg ?? defaultPushEnv(), [envArg]);
  const client = useQueryClient();
  const status = useMyNotificationsStatus();
  const support = useMemo(() => pushSupport(env), [env]);
  const supported = support === 'supported';
  const deviceQuery = usePushDeviceState(env, supported);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const device = supported ? deviceQuery.data?.subscription : null;
  const permission = deviceQuery.data?.permission ?? notificationPermission(env);

  const setDeviceState = useCallback(
    (next: PushDeviceState) => client.setQueryData<PushDeviceState>(PUSH_DEVICE_KEY, next),
    [client]
  );

  usePushResync(device, status.data?.subscribed === false && permission === 'granted');

  const enable = useCallback(async () => {
    const publicKey = status.data?.public_key;
    if (!publicKey || !supported) return false;
    setBusy(true);
    setError('');
    try {
      let current = notificationPermission(env);
      if (current !== 'granted') {
        current = await requestNotificationPermission(env);
        if (current !== 'granted') {
          const prev = client.getQueryData<PushDeviceState>(PUSH_DEVICE_KEY);
          setDeviceState({ permission: current, subscription: prev?.subscription ?? null });
          return false;
        }
      }
      let sub: PushSubscription;
      try {
        sub = await subscribeDevice(env, publicKey);
      } catch (e) {
        // KAN-845: сырой текст браузера только в консоль, пользователю понятный по-русски.
        console.error('PushManager.subscribe failed', e);
        setError(pushSubscribeErrorText(e));
        return false;
      }
      const data = await managerNotificationsApi.subscribe(subscriptionBody(sub));
      client.setQueryData(MY_NOTIFICATIONS_KEY, data);
      setDeviceState({ permission: 'granted', subscription: sub });
      return true;
    } catch (e) {
      setError(extractErrorMessage(e, 'Не удалось включить уведомления'));
      return false;
    } finally {
      setBusy(false);
    }
  }, [client, env, setDeviceState, status.data?.public_key, supported]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError('');
    // Backend подписку уже удалил: состояние обновляем, даже если отписка устройства упала.
    let backendDone = false;
    try {
      const sub = device ?? (await deviceSubscription(env));
      if (sub) {
        await managerNotificationsApi.unsubscribe(sub.endpoint);
        backendDone = true;
        await sub.unsubscribe();
      } else {
        backendDone = true;
      }
    } catch (e) {
      setError(extractErrorMessage(e, 'Не удалось выключить уведомления'));
    } finally {
      if (backendDone) {
        setDeviceState({ permission: notificationPermission(env), subscription: null });
        client.removeQueries({ queryKey: PUSH_RESYNC_KEY });
        void client.invalidateQueries({ queryKey: MY_NOTIFICATIONS_KEY });
      }
      setBusy(false);
    }
  }, [client, device, env, setDeviceState]);

  let view: PushView;
  if (!supported) view = support === 'ios-install' ? 'ios-install' : 'unsupported';
  else if (permission === 'denied') view = 'denied';
  else if (device === undefined || status.isLoading) view = 'loading';
  else view = device && permission === 'granted' ? 'on' : 'off';

  return { view, busy, error, enable, disable, status };
}
